"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { usePricingCatalog } from "@/hooks/usePricingCatalog";
import { FAQ_QUESTION_KEYS } from "@/lib/faq-keys";

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // Locale comes from next-intl's context (same as pricing-calculator)
  // instead of a prop, so every pricing consumer resolves it the same way
  // (#1013 review feedback). It picks and formats the pack price in the FAQ
  // answer — R$ 50,00 on pt-br, $10.00 on en-US — so the copy matches the
  // total Stripe charges in that currency (issue #1012).
  const locale = useLocale();
  const t = useTranslations("faq");
  // `pricingValues` ships empty strings while the catalog is still
  // resolving, so passing it straight through the `t()` placeholder API
  // never leaks the raw `{pricePerPack}` syntax to the user (review
  // feedback on #1013 — before this fix a slow first paint briefly showed
  // "…por {pricePerPack}—escolha…").
  const { pricingValues } = usePricingCatalog(locale);
  const faqItems = FAQ_QUESTION_KEYS;

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-muted">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            {t("eyebrow")}
          </p>
          <h2 className="text-3xl font-bold leading-tight text-foreground md:text-4xl">
            {t("title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t("description")}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqItems.map((key, index) => (
            <div
              key={key}
              className="mb-4 bg-card rounded-xl shadow-sm overflow-hidden transition-all duration-300"
            >
              <button
                type="button"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
                className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                onClick={() => toggleFAQ(index)}
              >
                <span className="font-medium text-foreground">
                  {t(`questions.${key}.question`)}
                </span>
                {openIndex === index ? (
                  <ChevronUp
                    className="text-primary"
                    size={20}
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronDown
                    className="text-muted-foreground"
                    size={20}
                    aria-hidden="true"
                  />
                )}
              </button>
              <div
                id={`faq-answer-${index}`}
                aria-hidden={openIndex !== index}
                className={`px-6 transition-all duration-300 ease-in-out ${
                  openIndex === index
                    ? "max-h-72 py-4"
                    : "max-h-0 overflow-hidden"
                }`}
              >
                <p className="text-muted-foreground">
                  {t(`questions.${key}.answer`, pricingValues)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
